// absolute imports
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, IconButton, Toolbar } from '@mui/material';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';

// relative imports
import { cardExpiryThresholdDays } from '../../../data/app-config/appDetails';
import usePaymentSourceExpiry from '../custom-hooks/usePaymentSourceExpiry';
import LedgibleButton from './buttons/LedgibleButton';

const useStyles = makeStyles()((theme, { isExpired }) => ({
  bar: {
    position: 'relative',
    height: 40,
    minHeight: '0px !important',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(1),
    color: theme.palette.common.white,
    backgroundColor: isExpired
      ? theme.palette.error.main
      : theme.palette.warning.main,
    boxSizing: 'content-box',
    transition: 'all 0.3s ease-in-out',
  },
  message: {
    fontSize: theme.typography.pxToRem(14),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  actionButton: {
    color: '#fff',
    borderColor: '#fff',
    backgroundColor: 'transparent',
    padding: theme.spacing(0.15, 1),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 400,
    '&:hover': {
      color: '#fff',
      backgroundColor: 'transparent',
      borderColor: '#fff',
    },
  },
  dismissButton: {
    position: 'absolute',
    right: 4,
    color: '#fff',
  },
}));

const buildExpiryMessage = ({ isExpired, last4, expiryLabel, canUpdate }) => {
  const cardLabel = last4 ? `ending in ${last4}` : 'on file';
  const verb = isExpired ? 'expired' : 'expires';
  const cta = canUpdate
    ? 'Update your payment method to avoid an interruption to your service.'
    : 'Contact your account admin to update it and avoid an interruption to your service.';
  return `Your card ${cardLabel} ${verb} ${expiryLabel}. ${cta}`;
};

const PaymentSourceExpiryAlert = ({
  variant,
  action,
  className,
  dismissible,
  thresholdDays,
}) => {
  const {
    show,
    shouldAlert,
    isExpired,
    expiryLabel,
    last4,
    hasCard,
    canUpdate,
    handleUpdatePayment,
    handleDismiss,
  } = usePaymentSourceExpiry({ thresholdDays });
  const { classes, cx } = useStyles({ isExpired });
  const message = buildExpiryMessage({
    isExpired,
    last4,
    expiryLabel,
    canUpdate,
  });

  // Alert variant sits next to card details already on screen — show whenever
  // the card needs attention, including while a preparer views a client.
  if (variant === 'alert') {
    if (!hasCard || !expiryLabel || !shouldAlert) {
      return null;
    }

    return (
      <Alert
        severity={isExpired ? 'error' : 'warning'}
        action={canUpdate ? action : null}
        className={className}
        sx={{ alignItems: 'center' }}
      >
        {message}
      </Alert>
    );
  }

  const canDismiss = dismissible ?? true;

  if (!show) {
    return null;
  }

  return (
    <Toolbar className={cx(classes.bar, className)}>
      <span className={classes.message}>{message}</span>
      {canUpdate && (
        <LedgibleButton
          size="small"
          variant="outlined"
          color="inherit"
          textTransformOff
          className={classes.actionButton}
          onClick={handleUpdatePayment}
          id="payment-source-expiry-update-btn"
        >
          Update
        </LedgibleButton>
      )}
      {canDismiss && (
        <IconButton
          size="small"
          className={classes.dismissButton}
          onClick={handleDismiss}
          aria-label="Dismiss payment alert"
          id="payment-source-expiry-dismiss-btn"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Toolbar>
  );
};

PaymentSourceExpiryAlert.propTypes = {
  variant: PropTypes.oneOf(['bar', 'alert']),
  action: PropTypes.node,
  className: PropTypes.string,
  dismissible: PropTypes.bool,
  thresholdDays: PropTypes.number,
};

PaymentSourceExpiryAlert.defaultProps = {
  variant: 'bar',
  action: null,
  className: '',
  dismissible: undefined,
  thresholdDays: cardExpiryThresholdDays,
};

export default PaymentSourceExpiryAlert;
